'use client';

import { Building2, MapPin, Shield, Bus, Utensils, ExternalLink, MessagesSquare } from 'lucide-react';
import { LucideIcon, type LucideIconComponent } from '@/src/components/LucideIcon';
import type { ApartmentReviews } from '@/src/lib/api/endpoints/apartmentReviews';
import * as styles from './redditReviews.styles';

const topicIcons = new Map<string, LucideIconComponent>([
  ['building', Building2],
  ['neighborhood', MapPin],
  ['safety', Shield],
  ['transit', Bus],
  ['lifestyle', Utensils],
]);

export default function RedditReviewsPanel({
  topics,
  onTopicClick,
}: {
  topics: NonNullable<ApartmentReviews['reddit']>['topics'];
  onTopicClick?: (topicId: string) => void;
}) {
  return (
    <div className={styles.stack}>
      <p>Explore Reddit discussions about this building and the surrounding area.</p>
      <div className={styles.topics}>
        {topics.map((topic) => {
          const icon = topicIcons.get(topic.icon);
          return (
            <a
              key={`${topic.topic}:${topic.url}`}
              className={styles.topicLink}
              href={topic.url}
              onClick={() => onTopicClick?.(topic.topic)}
              target="_blank"
              rel="nofollow noopener noreferrer"
              aria-label={`Search Reddit for ${topic.title} (opens in a new tab)`}
            >
              <span className={styles.topicIcon} data-topic={icon ? topic.icon : undefined} aria-hidden="true">
                <LucideIcon icon={icon ?? MessagesSquare} size="sm" />
              </span>
              <div className={styles.topicCopy}>
                <h3 className={styles.topicTitle}>{topic.title}</h3>
                <p className={styles.topicDescription}>{topic.description}</p>
              </div>
              <span className={styles.searchLabel} aria-hidden="true">
                Search
                <LucideIcon icon={ExternalLink} />
              </span>
            </a>
          );
        })}
      </div>
      <p className={styles.disclaimer}>Content and opinions are from Reddit users, not HotPads.</p>
    </div>
  );
}
